import { Banner, Container, Stack, StackItem, Tab, Tabs } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { DetailedUser, UserProfileResult } from '@mews-commander/data-access';
import { useUserContext } from '../../../context';
import { TabState, useUserDetailContext } from '../../../context/userDetailContext';
import { UserType } from '../../../types';
import { EditUserButton } from '../../EditUser';
import { EditUserProperties } from '../../EditUser/EditUserProperties';
import { useTableActionsInfo } from '../../../hooks/useTableActionsInfo';
import { UserDetailContentAvatar } from './UserDetailContentAvatar';
import { UserDetailContentProfileData } from './UserDetailContentProfileData';
import { UserDetailContentActivity } from './UserDetailContentActivity';

export const UserDetailContent = ({ user, userProfile }: { user: DetailedUser; userProfile: UserProfileResult }) => {
    const { isActionDisabled, isOwnProfile, hasNoProperties } = useTableActionsInfo(user);
    const { userType } = useUserContext();
    const { activeTab, setActiveTab } = useUserDetailContext();
    const userDetailTabs = getUserDetailTabs(userType);

    return (
        <Container padding={['0', '0', '500', '0']}>
            <Stack spacing="500" vertical>
                {isOwnProfile && (
                    <Banner
                        primaryText={<LocalizedText textKey={TextKey.UserManagementCannotEditOwnProfileBannerTitle} />}
                        secondaryText={
                            <LocalizedText textKey={TextKey.UserManagementCannotEditOwnProfileBannerContent} />
                        }
                        variant="info"
                    />
                )}

                {isActionDisabled && !isOwnProfile && hasNoProperties && (
                    <Banner
                        primaryText={
                            <LocalizedText textKey={TextKey.UserManagementCannotEditProvisionedUserBannerTitle} />
                        }
                        secondaryText={
                            <LocalizedText
                                textKey={TextKey.UserManagementCannotEditProvisionedUserNoPropertiesBannerText}
                            />
                        }
                        variant="info"
                    />
                )}

                <Stack spacing="200">
                    <UserDetailContentAvatar user={user} />
                    {!isActionDisabled && <EditUserButton userProfile={userProfile} />}
                </Stack>

                <Tabs
                    activeTabValue={activeTab}
                    inline
                    showDivider
                    onTabClick={(e, value) => setActiveTab(value as TabState)}
                >
                    {userDetailTabs.map(({ key, label }) => (
                        <Tab key={key} label={<LocalizedText textKey={label} />} value={key} id={key} />
                    ))}
                </Tabs>

                <StackItem>
                    {activeTab === TabState.General && <UserDetailContentProfileData user={user} />}
                    {activeTab === TabState.Devices && <UserDetailContentActivity user={user} />}
                    {activeTab === TabState.Properties && (
                        <EditUserProperties userId={user.userId} isReadOnly={isOwnProfile} />
                    )}
                </StackItem>
            </Stack>
        </Container>
    );
};

const commonTabs = [
    { key: TabState.General, label: TextKey.General },
    { key: TabState.Devices, label: TextKey.UserDetailContentActivityAndDevices },
];

function getUserDetailTabs(userType: UserType) {
    if (userType === UserType.Properties) {
        return [...commonTabs, { key: TabState.Properties, label: TextKey.PortfolioPropertyPlural }];
    }

    return commonTabs;
}
